import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import { toast } from '@/shared/hooks/useToast';
import {
  checkReplayable,
  clearFailedMutations,
  getCachedPendingCount,
  getFailedMutations,
  getPendingCount,
  getPendingMutations,
  isReplaying,
  isSupabaseMutation,
  replayMutations,
  stripLegacyFields,
  subscribeQueueChange,
  type FailedMutation,
  type PendingMutation,
  type ReplayOutcome,
  type SupabasePendingMutation,
} from '@/shared/lib/offlineMutationQueue';
import { monitoringService } from '@/shared/services/monitoringService';
import { ToastVariant } from '@/shared/types/toast.types';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * Slack added to a backoff timer so the entry is comfortably due by the time
 * the retry run reads `nextAttemptAt` again.
 */
const RETRY_TIMER_SLACK_MS = 250;

/**
 * Delay between an enqueue that happened while the browser was ONLINE and the
 * replay attempt it schedules.
 *
 * The transport-failure fallbacks in the three services queue a write whose
 * request has just died on the wire, and `navigator.onLine` never moves in that
 * case — no `online` event, so the `[isOnline]` effect below cannot re-run and
 * nothing else would ever drive a replay for the rest of the session. The short
 * delay is so the just-failed write is not re-issued into the same dead uplink
 * instantly; the queue's own exponential backoff takes over from there.
 */
export const ENQUEUE_SYNC_DELAY_MS = 3_000;

interface OfflineQueueState {
  /** Number of mutations waiting to sync */
  readonly pendingCount: number;
  /** List of pending mutations (for UI display) */
  readonly pendingMutations: readonly PendingMutation[];
  /** Mutations that will never be replayed again (dead-lettered) */
  readonly failedMutations: readonly FailedMutation[];
  /** Whether the queue is currently replaying (or armed to) */
  readonly isSyncing: boolean;
  /** Manually trigger a sync attempt */
  readonly syncNow: () => Promise<void>;
  /** Refresh the pending count from IndexedDB */
  readonly refreshCount: () => Promise<void>;
  /** Discard the dead-letter store after showing the user what is in it */
  readonly dismissFailed: () => Promise<void>;
}

/** `useSyncExternalStore` server snapshots — the queue is browser-only. */
function getZeroCount(): number {
  return 0;
}

function getNotSyncing(): boolean {
  return false;
}

/**
 * SQLSTATE classes a retry can never get past: data exceptions (`22`),
 * integrity constraint violations (`23`, e.g. a duplicate key) and access rule
 * violations (`42`, e.g. `42501` — the RLS `WITH CHECK` refusal). Anything else
 * (a wire failure, a 5xx, an expired JWT that a refresh will fix) is transient.
 */
const PERMANENT_SQLSTATE_CLASS = /^(?:22|23|42)/;

/** Classify a PostgREST error as worth another attempt, or terminal. */
function classifyPostgrestError(error: PostgrestError): ReplayOutcome {
  if (PERMANENT_SQLSTATE_CLASS.test(error.code)) {
    return { status: 'permanent', error: `${error.code}: ${error.message}` };
  }
  return { status: 'retryable', error: error.message };
}

/**
 * Zero affected rows on an UPDATE is not "nothing to do" — a `USING` clause
 * filters rows rather than raising, so PostgREST answers `error === null` with
 * an empty body for exactly the write RLS refused. It is also what a row
 * another member has since deleted looks like. Neither is retryable, and
 * neither may be counted as synced: the patch did not apply and never will.
 *
 * DELETE does not come through here, and that asymmetry is deliberate. Zero
 * rows there means the row is absent, which is precisely what the queued
 * mutation asked for — the ordinary case being the other parent deleting the
 * same task from their own device, or an `on delete cascade` from an album
 * that took its photos with it. Calling that a permanent failure produced a
 * destructive toast, a Sentry error and a standing "1 failed" badge for a
 * delete whose intent was fully satisfied.
 */
function zeroRowOutcome(mutation: SupabasePendingMutation): ReplayOutcome {
  return {
    status: 'permanent',
    error: `${mutation.op} on ${mutation.table} affected no rows — refused by row level security, or the row no longer exists`,
  };
}

/**
 * Replay a Supabase mutation through the typed client. Runs under the restored
 * authenticated session, so Row Level Security applies to the write.
 *
 * The client is cast to the schema-agnostic {@link SupabaseClient} so a
 * runtime-supplied table name and payload can be used generically. That is only
 * safe because the entry reached this function through {@link checkReplayable},
 * which has already proved the table is one of `REPLAYABLE_TABLES` and the
 * payload is a row object — the cast erases the schema types, so nothing
 * downstream would object to `from('anything')`.
 *
 * `stripLegacyFields` runs on the payload for the same reason: the entry was
 * serialised by whichever bundle the user was running when they went offline,
 * which may name a column a migration has since dropped.
 *
 * Update requests the affected rows back (`.select('id')`) because an
 * RLS-filtered write returns no error at all; see {@link zeroRowOutcome}.
 * Delete asks for them too, but only so the shape matches — a delete that
 * matched nothing has already got what it wanted.
 */
async function replaySupabaseMutation(
  mutation: SupabasePendingMutation
): Promise<ReplayOutcome> {
  const client = getSupabaseClient() as SupabaseClient;
  const table = client.from(mutation.table);

  switch (mutation.op) {
    case 'insert': {
      if (!mutation.payload) {
        return {
          status: 'permanent',
          error: 'Queued insert carries no payload',
        };
      }
      const payload = stripLegacyFields(mutation.table, mutation.payload);
      // Idempotent by primary key: a replay whose response was lost must not
      // fail on the duplicate its own earlier attempt created. Every queued
      // insert carries a client-generated `id`; anything without one falls back
      // to a plain insert.
      const result =
        typeof payload['id'] === 'string'
          ? await table.upsert(payload, { onConflict: 'id' })
          : await table.insert(payload);
      return result.error === null
        ? { status: 'success' }
        : classifyPostgrestError(result.error);
    }
    case 'update': {
      if (!mutation.payload || !mutation.matchId) {
        return { status: 'permanent', error: 'Queued update is incomplete' };
      }
      const payload = stripLegacyFields(mutation.table, mutation.payload);
      const result = await table
        .update(payload)
        .eq('id', mutation.matchId)
        .select('id');
      if (result.error !== null) {
        return classifyPostgrestError(result.error);
      }
      const affected = Array.isArray(result.data) ? result.data.length : 0;
      return affected > 0 ? { status: 'success' } : zeroRowOutcome(mutation);
    }
    case 'delete': {
      if (!mutation.matchId) {
        return { status: 'permanent', error: 'Queued delete has no target id' };
      }
      const result = await table
        .delete()
        .eq('id', mutation.matchId)
        .select('id');
      if (result.error !== null) {
        return classifyPostgrestError(result.error);
      }
      // Zero rows is success here — the row is gone, which is what was asked.
      // See {@link zeroRowOutcome} for why DELETE and UPDATE differ.
      return { status: 'success' };
    }
  }
}

/**
 * Replay a single queued mutation, after proving it is one this build can
 * replay at all.
 *
 * ## What happens to an entry this build does not understand
 *
 * It is DEAD-LETTERED, not dropped and not sent anywhere. `'permanent'` moves
 * the record into the `failed-mutations` store on the first answer and hands it
 * to `reportFailedMutations`, so the user gets a destructive toast naming their
 * own change and Sentry gets the record. Nothing is discarded quietly.
 *
 * That is the deliberate half of removing the old `'api'` branch. This function
 * used to end with `return replayApiMutation(mutation)` — a fallback that
 * caught every entry whose `kind` was not `'supabase'` and POSTed its stored
 * `endpoint` through the axios client, with the live Supabase access token
 * attached, to whatever host `VITE_API_BASE_URL` names. No build has ever
 * written such an entry (the `'api'` producer never had a caller, and this
 * database was created after the Supabase migration), so the branch protected
 * nothing and its only reachable use was a corrupted or hand-edited record.
 *
 * Deleting the branch without replacing it would have turned "sent to the wrong
 * host" into "vanished", which is the failure mode this whole queue exists to
 * prevent — the queue was already shipped once with a drain that silently left
 * writes behind. So the branch is gone AND the entry is still accounted for.
 */
async function replayMutation(
  mutation: PendingMutation
): Promise<ReplayOutcome> {
  const check = checkReplayable(mutation);
  if (!check.ok) {
    return { status: 'permanent', error: check.reason };
  }
  return replaySupabaseMutation(check.mutation);
}

/**
 * Report every dead-lettered mutation. A permanent failure is silent data loss
 * from the user's point of view — the row still renders as saved — so it gets a
 * destructive toast of its own (never the "Changes synced" one) and a Sentry
 * event carrying enough context to identify the refused write.
 */
function reportFailedMutations(failed: readonly FailedMutation[]): void {
  for (const record of failed) {
    monitoringService.captureError(
      new Error(`Offline mutation permanently failed: ${record.reason}`),
      {
        tags: {
          feature: 'offline-queue',
          // `'unknown'` is the honest tag for an entry that failed
          // `checkReplayable`: it says the queue could not classify the record,
          // which is exactly what needs to show up in Sentry. It used to read
          // `?? 'api'`, naming a transport that no longer exists and that
          // nothing ever enqueued.
          kind: record.mutation.kind ?? 'unknown',
          ...(isSupabaseMutation(record.mutation) && {
            table: record.mutation.table,
            op: record.mutation.op,
          }),
        },
        extra: {
          description: record.mutation.description,
          attempts: record.mutation.attempts ?? 0,
          queuedAt: new Date(record.mutation.timestamp).toISOString(),
        },
        level: 'error',
      }
    );
  }

  const first = failed[0];
  toast({
    variant: ToastVariant.Destructive,
    title:
      failed.length > 1
        ? `${failed.length} offline changes could not be saved`
        : 'An offline change could not be saved',
    description:
      first === undefined
        ? 'They were not written to the server.'
        : `„${first.mutation.description}“ — ${first.reason}`,
  });
}

/**
 * Report a queue-storage failure without letting it become an unhandled
 * rejection.
 *
 * `getDb` rejects rather than hanging when another tab is holding an older
 * version of the database open, so every queue read reachable from an effect
 * has a failure path now. Nothing user-facing is raised here: the write paths
 * surface their own failure through the store, and a badge that cannot refresh
 * is not worth a toast.
 */
function reportQueueStorageFailure(error: unknown): void {
  monitoringService.captureError(
    error instanceof Error ? error : new Error(String(error)),
    {
      tags: { feature: 'offline-queue', kind: 'storage' },
      level: 'warning',
    }
  );
}

/**
 * Hook to manage offline mutation queue sync lifecycle.
 *
 * Replays queued mutations when connectivity is restored. The replay is *not*
 * gated on a pending count: the count used to live in React state that nothing
 * refreshed on enqueue, so the `online` transition read a stale zero and
 * returned without replaying. `replayMutations` no-ops on an empty queue and is
 * single-flighted at module scope, so calling it unconditionally — from every
 * mounted consumer, on every `online` transition — is both correct and cheap.
 *
 * `pendingCount` and `isSyncing` are read straight off the queue module through
 * `useSyncExternalStore`, so the Header badge tracks enqueues live instead of
 * freezing at its mount-time value.
 */
export function useOfflineQueue(): OfflineQueueState {
  const isOnline = useOnlineStatus();
  const pendingCount = useSyncExternalStore(
    subscribeQueueChange,
    getCachedPendingCount,
    getZeroCount
  );
  const isSyncing = useSyncExternalStore(
    subscribeQueueChange,
    isReplaying,
    getNotSyncing
  );
  const [pendingMutations, setPendingMutations] = useState<PendingMutation[]>(
    []
  );
  const [failedMutations, setFailedMutations] = useState<FailedMutation[]>([]);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncRef = useRef<() => void>(() => undefined);

  const refreshCount = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingMutations(count > 0 ? await getPendingMutations() : []);
      setFailedMutations(await getFailedMutations());
    } catch (error) {
      // Storage is unreachable (private mode, or an upgrade another tab is
      // blocking). The badge simply stays where it is; nothing here is worth
      // an unhandled rejection.
      reportQueueStorageFailure(error);
    }
  }, []);

  const syncNow = useCallback(async () => {
    let summary;
    try {
      summary = await replayMutations(replayMutation);
    } catch (error) {
      reportQueueStorageFailure(error);
      return;
    }
    if (summary.skipped) return;

    // A run finished, so any wake-up timer left over from an earlier one is
    // stale. Cleared HERE rather than before the replay: clearing it up front
    // discarded a scheduled retry whenever this call turned out to be the one
    // that got skipped.
    if (retryTimer.current !== null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }

    if (summary.synced > 0) {
      toast({
        title: 'Changes synced',
        description: `${summary.synced} offline change${summary.synced > 1 ? 's' : ''} synced successfully.`,
      });
    }

    if (summary.failed.length > 0) {
      reportFailedMutations(summary.failed);
    }

    // Entries held back by backoff need a wake-up; nothing else would retry
    // them until the next `online` transition or page load.
    if (summary.retryDelayMs !== null) {
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        syncRef.current();
      }, summary.retryDelayMs + RETRY_TIMER_SLACK_MS);
    }

    await refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    syncRef.current = () => {
      void syncNow();
    };
  }, [syncNow]);

  // Keep the mutation lists in step with every queue change. `pendingCount`
  // and `isSyncing` need no help here — they are synchronous reads of the same
  // store — so this only re-reads the two lists, and it sits out a replay run:
  // a run mutates the queue once per entry, and the transition back to idle
  // notifies too, which is the notification that matters.
  useEffect(() => {
    void refreshCount();
    return subscribeQueueChange(() => {
      if (isReplaying()) return;
      void refreshCount();
    });
  }, [refreshCount]);

  // Auto-replay on mount and on every `online` transition. Deliberately not
  // gated on `pendingCount` — that gate is the blocker this hook used to carry.
  useEffect(() => {
    if (!isOnline) return;
    void syncNow();
  }, [isOnline, syncNow]);

  // The other replay driver, and the one an `online` transition cannot supply:
  // a write that failed on the wire while the browser still reported "online"
  // is enqueued without `navigator.onLine` ever moving, so the effect above
  // cannot re-run and the entry would sit in IndexedDB for the rest of the
  // session. Keyed on the live count, which the queue moves on every enqueue.
  //
  // It cannot loop. A successful or dead-lettered entry drops the count toward
  // zero, where the effect returns early; a retryable one leaves the count
  // where it was, so the dependency does not move and the run's own backoff
  // timer is what tries again.
  useEffect(() => {
    if (!isOnline || pendingCount === 0) return;
    const timer = setTimeout(() => {
      syncRef.current();
    }, ENQUEUE_SYNC_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [isOnline, pendingCount]);

  useEffect(
    () => () => {
      if (retryTimer.current !== null) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    },
    []
  );

  const dismissFailed = useCallback(async () => {
    await clearFailedMutations();
    setFailedMutations([]);
  }, []);

  return {
    pendingCount,
    pendingMutations,
    failedMutations,
    isSyncing,
    syncNow,
    refreshCount,
    dismissFailed,
  };
}
